import React, { useEffect, useState } from 'react';
import { AppLayout, Modal, Tabs } from '@amzn/awsui-components-react';
import SiteDevices from './SiteDevices';
import SelectSite from './SelectSite';
import { useDispatch, useSelector } from 'react-redux';
import { Privileges } from 'src/utils/UserSitesUtils';
import {
  selectUsername,
  selectUserPrefs,
  selectUserSelectedSitePrivs,
  selectUserSelectedSite,
  setUserPrefs } from '../stores/slices/userSlice';
import { updateUserPrefs } from 'src/utils/UserPrefsUtils';
import { fetchDevices } from '../utils/DevicesUtils';
import {
  setInputsDevices,
  setReadersAlarmsDevices,
  setReadersModesDevices,
  setLoadingDevices,
  setSelectedReadersModes,
  setSelectedInputs,
  setSelectedReadersAlarms,
  selectSelectedInputs,
  selectSelectedReadersAlarms,
  selectSelectedReadersModes,
  updateSelectedInputMask,
  updateSelectedReaderMasked,
  updateSelectedReaderMode,
  } from '../stores/slices/devicesSlice';
import { TopNav } from './Navigation';
import Tracker from './Tracker';
import { IsItDown } from './IsItDown';
import { debug } from '../utils/commonUtils';

export default function App(props: any) {
  debug(`App() props is ${JSON.stringify(props)}`);

  const dispatch = useDispatch();

  const username = useSelector(selectUsername);
  const userPrefs = useSelector(selectUserPrefs);
  const userSelectedSite = useSelector(selectUserSelectedSite);
  const userSelectedSitePrivs = useSelector(selectUserSelectedSitePrivs);
  const selectedInputs = useSelector(selectSelectedInputs);
  const selectedReadersAlarms = useSelector(selectSelectedReadersAlarms);
  const selectedReadersModes = useSelector(selectSelectedReadersModes);

  const [siteSelectVisible, setSiteSelectVisible] = React.useState<boolean>(false);
  const [loadingSites, setLoadingSites] = React.useState<boolean>(false);

  async function refreshDevices() {
    debug(`App() refreshDevices() userSelectedSite.sitename is ${userSelectedSite.sitename} userSelectedSite.siteRegion is ${userSelectedSite.siteRegion}`);
    dispatch(setLoadingDevices(true));
    try {
      let returnedDevices = await fetchDevices(userSelectedSite.sitename, userSelectedSite.siteRegion);
      const inputs = returnedDevices.filter(
        d => (d.Device_Type === 'alarm_panel_input'
          || d.Device_Type === 'card_reader_aux_input_1'
          || d.Device_Type === 'card_reader_aux_input_2')
      );
      const readers = returnedDevices.filter(d => d.Device_Type === 'card_reader');
      dispatch(setInputsDevices( { inputs: inputs } ));
      debug(`App() refreshDevices() selectedInputs.length is ${selectedInputs.length} inputs.length is ${inputs.length}`);
      if (selectedInputs) inputs.forEach(input => dispatch(updateSelectedInputMask(input)));
      dispatch(setReadersAlarmsDevices( { readersAlarms: readers } ));
      if (selectedReadersAlarms) readers.forEach(reader => dispatch(updateSelectedReaderMasked(reader)));
      dispatch(setReadersModesDevices( { readersModes: readers } ));
      if (selectedReadersModes) readers.forEach(reader => dispatch(updateSelectedReaderMode(reader)));
    } catch(error) {
      console.error(`refreshDevices() error is ${error} JSON.stringify is ${JSON.stringify(error)}`);
    }
    dispatch(setLoadingDevices(false));
  };

  const [ darkMode, setDarkMode ] = useState<boolean>(JSON.parse(userPrefs.site)['darkMode'] || false);
  if (darkMode) {
    document.body.classList.add('awsui-polaris-dark-mode');
  } else if (!darkMode) {
    document.body.classList.remove('awsui-polaris-dark-mode');
  }

  useEffect(() => {
    debug(`App() useEffect() [darkMode]`);
    const sitePrefs = JSON.parse(userPrefs?.site);
    debug(`App() useEffect() [darkMode] sitePrefs is ${JSON.stringify(sitePrefs)}`);
    if (!sitePrefs) return;
    sitePrefs['darkMode'] = darkMode;
    const newUserPrefs = {
      ...userPrefs,
      site: JSON.stringify(sitePrefs)
    };
    dispatch(setUserPrefs(newUserPrefs));
    updateUserPrefs(newUserPrefs);
  },[darkMode]);

  useEffect(() => {
    if (userSelectedSite) (async () => {
      await refreshDevices();
      dispatch(setSelectedInputs([]));
      dispatch(setSelectedReadersAlarms([]));
      dispatch(setSelectedReadersModes([]));
    })();
  },[userSelectedSite]);

  const refreshUserSites = async () => {
    debug(`App(): refreshUserSites()`);
    setLoadingSites(true);
    try {
      if (props.refreshUserSitesCallback) await props.refreshUserSitesCallback();
    } catch(error) {
      debug(`App(): refreshUserSites() error is ${error} ${typeof error === 'object' ? JSON.stringify(error) : error}`);
    }
    setLoadingSites(false);
    debug(`App(): refreshUserSites() complete`);
  };

  const selectSiteOnDismissHandler = () => {
    setSiteSelectVisible(false);
  };

  const displayTabs = (): {label: string, id: string, content: any }[] => {
    let tabs: { label: string; id: string; content: any; }[] = [];
    if (userSelectedSitePrivs && userSelectedSitePrivs.length > 0 && userSelectedSitePrivs.includes(Privileges.all))
      tabs = [
        {
          label: "Inputs",
          id: "inputs",
          content: <SiteDevices deviceType='inputs' refreshDevicesCallback={refreshDevices} />
        },
        {
          label: "Readers - Alarms",
          id: "readersAlarms",
          content: <SiteDevices deviceType='readersAlarms' refreshDevicesCallback={refreshDevices} />
        },
        {
          label: "Readers - Mode",
          id: "readersModes",
          content: <SiteDevices deviceType='readersModes' refreshDevicesCallback={refreshDevices} />
        }
      ];
    else
      tabs = [
        {
          label: "Inputs",
          id: "inputs",
          content: <SiteDevices deviceType='inputs' refreshDevicesCallback={refreshDevices} />
        }
      ];
    return tabs;
  };

  return(
    <>
      <TopNav
        setSiteSelectVisibleCallback={setSiteSelectVisible}
        sitename={userSelectedSite?.sitename}
        sitePrivs={userSelectedSitePrivs}
        username={username}
        darkMode={darkMode}
        setDarkModeCallback={setDarkMode}
      />
      <AppLayout
        ariaLabels={{ navigationClose: 'close' }}
        content={
          <Tabs
            tabs={displayTabs()}
            variant="container"
          />
        }
        contentType="table"
        headerSelector="#header"
        navigationHide
        notifications={<IsItDown />}
        stickyNotifications
        toolsHide={true}
      />
      <Tracker />
      <Modal visible={siteSelectVisible} size='medium' header='Change Site' onDismiss={selectSiteOnDismissHandler}>
        <SelectSite
          refreshUserSitesCallback={refreshUserSites}
          selectedSite={userSelectedSite}
          siteRequiredAlertVisible={false}
          siteSelectVisible={siteSelectVisible}
          setSiteSelectVisibleCallback={setSiteSelectVisible}
          loadingSites={loadingSites}
        />
        </Modal>
        </>
  );
  
}